import { useMemo } from 'react';
import { EmployeeIdpStatusBadge } from '@/common/components/shared/badge';
import { EmployeeIdpNextReviewDateCell } from '@/common/components/shared/date-cell';
import { EmployeeIdpLink } from '@/common/components/shared/link';
import { NoResult } from '@/common/components/shared/no-result';
import { Table, type TableLayout } from '@/common/components/shared/table';
import { DATETIME_FORMATS, DateUtils } from '@/common/utils';
import type { DashboardEmployeeIdp } from '../../types';

export interface EmployeeIdpTableProps {
  currentEmployeeId: string;
  data: Array<DashboardEmployeeIdp>;
  loading: boolean;
}

export const EmployeeIdpTable = ({ currentEmployeeId, data, loading }: EmployeeIdpTableProps) => {
  const sortedData = useMemo(
    () =>
      [...data].sort(
        (a, b) =>
          a.startDate.localeCompare(b.startDate) ||
          a.employee.generalInformation.preferredFullName.localeCompare(
            b.employee.generalInformation.preferredFullName,
          ),
      ),
    [data],
  );

  const layout = useMemo(
    () =>
      [
        {
          headerRender: () => 'Employee',
          accessor: 'employee',
          truncated: false,
          cellRender: ({ id, employee }) => (
            <EmployeeIdpLink className="text-nowrap" idpId={id}>
              {employee.generalInformation.preferredFullName}
            </EmployeeIdpLink>
          ),
        },
        {
          headerRender: () => 'Status',
          accessor: 'status',
          cellRender: ({ status, type, completedReviewCount }) => (
            <EmployeeIdpStatusBadge status={status} reviewing={{ type, completedReviewCount }} />
          ),
        },
        {
          headerRender: () => 'Next Review Date',
          accessor: 'nextReviewDate',
          thClassName: 'text-right',
          tdClassName: 'text-right tabular-nums',
          cellRender: ({ nextReviewDate }) => <EmployeeIdpNextReviewDateCell nextReviewDate={nextReviewDate} />,
        },
        {
          // FR-020a: the "employee" role sees End Date, "line manager"/"HR representative" sees Deadline.
          // employeeId can never equal lineManagerId/hrRepresentativeId on the same record, so this
          // binary check is sufficient — there is no three-way tie to break. The header is a single
          // static column (headerRender takes no row), so each row self-labels its own value instead.
          headerRender: () => 'End Date / Deadline',
          accessor: 'endDate',
          thClassName: 'text-right',
          tdClassName: 'text-right tabular-nums',
          cellRender: ({ employeeId, endDate, reviewDeadlineDate }) => {
            const isOwnEmployeeRow = employeeId === currentEmployeeId;

            return (
              <span>
                <span className="text-gray-7">{isOwnEmployeeRow ? 'End Date: ' : 'Deadline: '}</span>
                {DateUtils.formatDate(isOwnEmployeeRow ? endDate : reviewDeadlineDate, DATETIME_FORMATS.MMMddyyyy)}
              </span>
            );
          },
        },
      ] as Array<TableLayout<DashboardEmployeeIdp>>,
    [currentEmployeeId],
  );

  return (
    <Table<DashboardEmployeeIdp>
      allowFloatingHeader={true}
      data={sortedData}
      headerClassName="h-auto [&>th]:py-2"
      hoverable={false}
      layout={layout}
      loading={loading}
      notFoundTemplate={
        <NoResult title="No data available" titleClassName="text-sm text-gray-9" wrapperClassName="py-10" />
      }
      numOfStickyCols={0}
      page={1}
      scrollContainerClassName="max-h-[250px]"
      skeletonCount={3}
      wrapperClassName="rounded border-x border-b overflow-auto"
    />
  );
};
